import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Normal map generation utilities
// ---------------------------------------------------------------------------

export const S = 512; // standard texture resolution

// ---- Texture helpers -------------------------------------------------------

export function finishTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  return tex;
}

// ---- Normal map generation -------------------------------------------------

/**
 * Generate a normal map from a grayscale height map canvas.
 * Uses Sobel-like gradients. (128,128,255) = flat normal.
 */
export function generateNormalMap(
  heightCanvas: HTMLCanvasElement,
  strength = 2.0,
): THREE.CanvasTexture {
  const w = heightCanvas.width;
  const h = heightCanvas.height;
  const srcCtx = heightCanvas.getContext('2d')!;
  const srcData = srcCtx.getImageData(0, 0, w, h).data;

  const heights = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const ri = srcData[i * 4], gi = srcData[i * 4 + 1], bi = srcData[i * 4 + 2];
    heights[i] = (ri * 0.299 + gi * 0.587 + bi * 0.114) / 255;
  }

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const outCtx = out.getContext('2d')!;
  const outImg = outCtx.createImageData(w, h);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tl = heights[((y - 1 + h) % h) * w + ((x - 1 + w) % w)];
      const t  = heights[((y - 1 + h) % h) * w + x];
      const tr = heights[((y - 1 + h) % h) * w + ((x + 1) % w)];
      const l  = heights[y * w + ((x - 1 + w) % w)];
      const r  = heights[y * w + ((x + 1) % w)];
      const bl = heights[((y + 1) % h) * w + ((x - 1 + w) % w)];
      const bm = heights[((y + 1) % h) * w + x];
      const br = heights[((y + 1) % h) * w + ((x + 1) % w)];

      const dx = (tr + 2 * r + br) - (tl + 2 * l + bl);
      const dy = (bl + 2 * bm + br) - (tl + 2 * t + tr);

      let nx = -dx * strength;
      let ny = -dy * strength;
      let nz = 1.0;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx /= len; ny /= len; nz /= len;

      const idx = (y * w + x) * 4;
      outImg.data[idx]     = Math.floor((nx * 0.5 + 0.5) * 255);
      outImg.data[idx + 1] = Math.floor((ny * 0.5 + 0.5) * 255);
      outImg.data[idx + 2] = Math.floor((nz * 0.5 + 0.5) * 255);
      outImg.data[idx + 3] = 255;
    }
  }

  outCtx.putImageData(outImg, 0, 0);
  const tex = new THREE.CanvasTexture(out);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  return tex;
}

/** Generate normal map directly from a Float32Array heightfield (0..1) */
export function normalFromHeightfield(
  heights: Float32Array,
  w: number,
  h: number,
  strength: number,
): THREE.CanvasTexture {
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const outCtx = out.getContext('2d')!;
  const outImg = outCtx.createImageData(w, h);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tl = heights[((y - 1 + h) % h) * w + ((x - 1 + w) % w)];
      const t  = heights[((y - 1 + h) % h) * w + x];
      const tr = heights[((y - 1 + h) % h) * w + ((x + 1) % w)];
      const l  = heights[y * w + ((x - 1 + w) % w)];
      const r  = heights[y * w + ((x + 1) % w)];
      const bl = heights[((y + 1) % h) * w + ((x - 1 + w) % w)];
      const bm = heights[((y + 1) % h) * w + x];
      const br = heights[((y + 1) % h) * w + ((x + 1) % w)];

      const dx = (tr + 2 * r + br) - (tl + 2 * l + bl);
      const dy = (bl + 2 * bm + br) - (tl + 2 * t + tr);

      let nx = -dx * strength;
      let ny = -dy * strength;
      let nz = 1.0;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx /= len; ny /= len; nz /= len;

      const idx = (y * w + x) * 4;
      outImg.data[idx]     = Math.floor((nx * 0.5 + 0.5) * 255);
      outImg.data[idx + 1] = Math.floor((ny * 0.5 + 0.5) * 255);
      outImg.data[idx + 2] = Math.floor((nz * 0.5 + 0.5) * 255);
      outImg.data[idx + 3] = 255;
    }
  }

  outCtx.putImageData(outImg, 0, 0);
  const tex = new THREE.CanvasTexture(out);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  return tex;
}
